import { memo, useEffect, useState } from 'react';
import { Download, ImageOff } from 'lucide-react';
import { FILE_KIND_META, IconButton, cn, fileKindFor } from '@cyphex/ui';
import type { MessageAttachment } from '@cyphex/types';
/** Deep import: the Drive barrel is a lazy route chunk, this is the chat pane. */
import { useContentSessionReady } from '@/features/files/hooks/useContentSession';
import { formatBytes } from '../../lib/upload';

interface AttachmentGridProps {
  attachments: readonly MessageAttachment[];
  className?: string;
}

const MAX_INLINE_IMAGES = 4;

/**
 * One inline image preview.
 *
 * `thumbnailUrl` FIRST, and it now resolves for every image: the API points it
 * at `/v1/files/:id/thumbnail`, which serves the generated derivative, falls
 * back to the original while that is small, and 404s rather than pushing a
 * 12 MP original into a 480-pixel cell. `downloadUrl` is the second chance, and
 * the glyph is the third — a broken-image icon is never an acceptable answer.
 *
 * Both urls authorise through the httpOnly content ticket. An `<img>` cannot
 * send an Authorization header, which is exactly why every one of these used to
 * render broken.
 *
 * And the ticket has to be IN THE JAR before the src is set. `onError` walks
 * this list one step at a time and never walks back, so a 401 caused purely by
 * arriving ~30ms ahead of the mint does not degrade the image, it CONSUMES the
 * thumbnail and shows the full-size original instead — or, for the last source,
 * gives up entirely. `contentReady` closes that window.
 */
function InlineImage({ attachment }: { attachment: MessageAttachment }) {
  const { file } = attachment;
  const sources = [file.thumbnailUrl, file.downloadUrl].filter(
    (url): url is string => url !== null && url !== '',
  );
  const [index, setIndex] = useState(0);
  const contentReady = useContentSessionReady();
  // A fresh ticket earns the preferred source another try.
  useEffect(() => setIndex(0), [contentReady]);
  const src = sources[index];

  if (src === undefined) {
    return (
      <span className="flex aspect-[8/5] w-full items-center justify-center rounded-lg border border-border bg-subtle text-muted-foreground">
        <ImageOff size={20} aria-label={`${file.name} could not be shown`} />
      </span>
    );
  }

  // Placeholder, not a broken request. Same box, so nothing reflows when the
  // ticket lands a round trip later.
  if (!contentReady) {
    return (
      <span
        aria-label={`Loading ${file.name}`}
        className="block aspect-[8/5] w-full animate-pulse rounded-lg border border-border bg-subtle"
      />
    );
  }

  return (
    <a
      href={file.downloadUrl ?? '#'}
      target="_blank"
      rel="noreferrer noopener"
      className="group relative block overflow-hidden rounded-lg border border-border"
    >
      <img
        src={src}
        alt={file.name}
        width={480}
        height={300}
        loading="lazy"
        decoding="async"
        onError={() => setIndex((current) => current + 1)}
        className="aspect-[8/5] w-full object-cover transition-transform duration-slow group-hover:scale-[1.02]"
      />
    </a>
  );
}

/** Inline image previews plus file chips. Images lazy-load at a fixed ratio. */
export const AttachmentGrid = memo(function AttachmentGrid({ attachments, className }: AttachmentGridProps) {
  if (attachments.length === 0) return null;

  const images = attachments.filter((item) => item.file.fileClass === 'image');
  const others = attachments.filter((item) => item.file.fileClass !== 'image');

  return (
    <div className={cn('space-y-2', className)}>
      {images.length > 0 ? (
        <div
          className={cn(
            'grid gap-1.5',
            /* One photo sits at a Teams-like size; a banner-wide image pushed
               the conversation around it off the screen. */
            images.length === 1 ? 'max-w-sm grid-cols-1' : 'max-w-bubble grid-cols-2',
          )}
        >
          {images.slice(0, MAX_INLINE_IMAGES).map((item) => (
            <InlineImage key={item.id} attachment={item} />
          ))}
        </div>
      ) : null}

      {others.map((item) => {
        const kind = fileKindFor(item.file.name);
        const meta = FILE_KIND_META[kind];
        const Glyph = meta.icon;
        return (
          <div
            key={item.id}
            className="flex max-w-bubble items-center gap-2.5 rounded-lg border border-border bg-card p-2.5"
          >
            <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-md', meta.tileClass)}>
              <Glyph size={20} className={meta.colorClass} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-md font-medium text-foreground">{item.file.name}</span>
              <span className="block text-sm text-muted-foreground">
                {formatBytes(item.file.sizeBytes)}
              </span>
            </span>
            <IconButton
              icon={Download}
              label={`Download ${item.file.name}`}
              size="sm"
              variant="ghost"
              onClick={() =>
                window.open(
                  item.file.downloadUrl === null ? '#' : `${item.file.downloadUrl}?download=true`,
                  '_blank',
                  'noopener',
                )
              }
            />
          </div>
        );
      })}
    </div>
  );
});
